// "Show me only the rows tagged …" — the control, and the state behind it.
//
// WHY IT EXISTS
//
// `tags` is on a site file and on a section, it is a free list of words the
// author types, and it is the only handle either row has for a distinction the
// service knows nothing about. A per-locale asset is the case that asked for
// this (P206): a page in Spanish overrides `config.values.heroImage` with a
// different file, and that file is a different upload with a different id — so
// the ONE thing that can say which of forty images is the Spanish one is a tag
// on it. The picker listed all forty in upload order.
//
// A locale code is a tag like any other here, and that is on purpose. Nothing
// in this service reads a tag, no code is reserved and none is validated, so a
// site can file its images by language, by campaign or by both without asking
// anybody for a convention first.
//
// WHY IT IS CLIENT-SIDE
//
// Neither list has a tag argument on the wire — a file listing takes `kind`
// and a section listing takes nothing — and this needs no schema change, no
// index and no new query to be useful, because both listings already SELECT
// `tags` on the fragment they render. What was missing was the narrowing, and
// the rows for it are already in the browser.
//
// The honest limit of that, said once here rather than in each caller: this
// filters what has been FETCHED. Both lists page on a cursor, so on a site
// with more rows than one page a tag can have matches further down that are
// only reachable by loading more. The control says nothing about that; the
// list's own "Load more" is unchanged and still follows the server's cursor.
import React, { useCallback, useMemo, useState } from "react";
import { Col, Input, Label, Row } from "reactstrap";

// A stable identity for "nothing yet", so the first answer is the first render
// that changes anything.
const NONE = [];

// Every tag on any of these rows, once each, in a stable order.
//
// Sorted rather than left in row order: it is a picker, and a list that
// reshuffles when a file is renamed is one nobody can find anything in twice.
export const tagsOf = (items) => {
  const seen = new Set();

  for (const item of items || []) {
    for (const tag of (item && item.tags) || []) {
      if (typeof tag === "string" && tag) {
        seen.add(tag);
      }
    }
  }

  return [...seen].sort();
};

// The predicate the list applies. Exact, not a prefix or a substring: a tag is
// a word somebody chose, `es` must not match `estimates`, and a picker whose
// options come from the rows themselves has no need to guess.
export const hasTag = (tag) => (item) =>
  ((item && item.tags) || []).includes(tag);

// The state a filtered list needs, in the shape its three props want.
//
// `onData` is the list's own callback and reports the WHOLE page — see
// list-cursor, which filters at render for exactly this reason — so the
// choices stay complete while a choice is made.
export const useTagFilter = () => {
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState(NONE);

  const onData = useCallback((items) => {
    setTags((previous) => {
      const next = tagsOf(items);

      // `onData` fires on every page and on every refetch, and a new array
      // each time would re-render the screen that owns this — which, for the
      // file picker, is a modal rendered by the widget behind it (see
      // ../file-selector on what remounts there cost).
      return previous.length === next.length &&
        previous.every((value, index) => value === next[index])
        ? previous
        : next;
    });
  }, []);

  // `null` rather than a predicate that lets everything through: list-cursor
  // skips the pass entirely for a falsy filter, and an unfiltered list should
  // not walk its own rows to prove it.
  const filter = useMemo(() => (tag ? hasTag(tag) : null), [tag]);

  return { tag, tags, setTag, onData, filter, active: Boolean(tag) };
};

// The control. Renders NOTHING when no row carries a tag, which is every site
// that has not started using them — a picker offering only "All" is a row of
// furniture that explains itself to nobody.
export const TagFilter = ({ state: { tag, tags, setTag }, label = "Tag" }) => {
  // A tag that was chosen and has since gone from every row — the last file
  // carrying it was renamed or removed — stays in the list while it is the
  // selection. Dropping it would leave the control showing "All" over a list
  // that is still filtered to nothing.
  const options = tag && !tags.includes(tag) ? [tag, ...tags] : tags;

  if (options.length === 0) {
    return null;
  }

  return (
    <Row className="mb-3">
      <Col md={6}>
        <Label className="text-muted small mb-1" for="tag-filter">
          {label}
        </Label>
        <Input
          id="tag-filter"
          type="select"
          bsSize="sm"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          <option value="">All</option>
          {options.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Input>
      </Col>
    </Row>
  );
};
